import * as THREE from "three";
import {
  EYE_HEIGHT,
  FLY_SPEED,
  FLY_VERTICAL,
  GRAVITY,
  JUMP_VELOCITY,
  MAX_HEALTH,
  MAX_HUNGER,
  MOUSE_SENS,
  SEA_LEVEL,
  SNEAK_SPEED,
  SPRINT_SPEED,
  SWIM_SPEED,
  THIRD_PERSON_DIST,
  WALK_SPEED,
  WATER_GRAVITY,
} from "../config";
import { Block, blockDef, isReplaceable, isSolid } from "../engine/blocks";
import type { Input } from "../engine/input";
import { wishVelocity } from "../engine/move";
import { boxOverlapsWater, moveWithCollisions, playerBox } from "../engine/physics";
import { raycastVoxel, type RayHit } from "../engine/raycast";
import type { World } from "../engine/world";
import { breakSpeed, canHarvest, itemDef } from "./items";
import { Inventory } from "./inventory";
import type { Containers } from "./containers";
import { CharacterView } from "./character";

export type OpenUi = "none" | "inventory" | "crafting" | "chest" | "furnace";

export class Player {
  x = 0;
  y = 80;
  z = 0;
  vx = 0;
  vy = 0;
  vz = 0;
  yaw = 0;
  pitch = 0.28;
  flying = false;
  thirdPerson = true;
  grounded = false;
  swimming = false;
  sneaking = false;
  sprinting = false;
  health = MAX_HEALTH;
  hunger = MAX_HUNGER;
  invincibleUntil = 0;
  breakTime = 0;
  breakTarget: RayHit | null = null;
  target: RayHit | null = null;
  readonly inventory = new Inventory();
  readonly character = new CharacterView();
  open: OpenUi = "none";
  openPos: { x: number; y: number; z: number } | null = null;
  fallStart = 80;
  time = 0;
  eatTime = 0;
  hurtFlash = 0;

  spawn(x: number, y: number, z: number): void {
    this.x = x;
    this.y = y;
    this.z = z;
    this.fallStart = y;
    this.character.group.position.set(x, y, z);
  }

  eye(): THREE.Vector3 {
    return new THREE.Vector3(this.x, this.y + EYE_HEIGHT, this.z);
  }

  lookDir(): THREE.Vector3 {
    const cp = Math.cos(this.pitch);
    return new THREE.Vector3(-Math.sin(this.yaw) * cp, -Math.sin(this.pitch), -Math.cos(this.yaw) * cp);
  }

  update(
    dt: number,
    input: Input,
    world: World,
    containers: Containers,
    playing: boolean,
    onBreak: (hit: RayHit) => void,
    onPlace: (x: number, y: number, z: number, id: number) => void,
    onInteract: (hit: RayHit) => void,
    onPunch: () => boolean,
  ): void {
    this.time += dt;
    if (this.hurtFlash > 0) this.hurtFlash -= dt;
    if (!playing) {
      this.character.update(dt, false, true, this.flying, false, this.yaw, this.pitch, this.inventory.armor, null);
      this.character.group.position.set(this.x, this.y, this.z);
      return;
    }

    const look = input.consumeLook();
    this.yaw -= look.dx * MOUSE_SENS;
    this.pitch += look.dy * MOUSE_SENS;
    this.pitch = THREE.MathUtils.clamp(this.pitch, -1.45, 1.45);

    const wheel = input.consumeWheel();
    if (wheel) {
      this.inventory.selected = (this.inventory.selected + wheel + 9) % 9;
    }
    for (let i = 0; i < 9; i++) {
      if (input.pressed(`Digit${i + 1}`)) this.inventory.selected = i;
    }

    if (this.open !== "none") return;

    this.sneaking = input.pressed("ControlLeft") || input.pressed("ControlRight") || input.pressed("KeyC");
    this.sprinting = (input.pressed("ShiftLeft") || input.pressed("ShiftRight")) && !this.sneaking;

    const box = playerBox(this.x, this.y, this.z);
    this.swimming = boxOverlapsWater(world, box);

    const forward = input.pressed("KeyW") || input.pressed("ArrowUp") ? 1 : 0;
    const back = input.pressed("KeyS") || input.pressed("ArrowDown") ? 1 : 0;
    const left = input.pressed("KeyA") || input.pressed("ArrowLeft") ? 1 : 0;
    const right = input.pressed("KeyD") || input.pressed("ArrowRight") ? 1 : 0;
    const jump = input.pressed("Space");
    const down = this.sneaking;

    let speed = WALK_SPEED;
    if (this.sneaking) speed = SNEAK_SPEED;
    else if (this.sprinting) speed = SPRINT_SPEED;
    if (this.flying) speed = this.sprinting ? FLY_SPEED * 1.6 : FLY_SPEED;
    if (this.swimming && !this.flying) speed = SWIM_SPEED;

    const wish = wishVelocity(this.yaw, right - left, forward - back, speed);
    const mx = wish.x;
    const mz = wish.z;

    if (this.flying) {
      this.vx = mx;
      this.vz = mz;
      this.vy = 0;
      if (jump) this.vy += FLY_VERTICAL;
      if (down) this.vy -= FLY_VERTICAL;
    } else if (this.swimming) {
      this.vx = THREE.MathUtils.lerp(this.vx, mx, 1 - Math.pow(0.02, dt));
      this.vz = THREE.MathUtils.lerp(this.vz, mz, 1 - Math.pow(0.02, dt));
      this.vy -= WATER_GRAVITY * dt;
      this.vy *= Math.pow(0.86, dt * 60);
      if (jump) this.vy += 18 * dt;
      if (down) this.vy -= 12 * dt;
    } else {
      this.vx = mx;
      this.vz = mz;
      this.vy -= GRAVITY * dt;
      if (this.grounded && jump) {
        this.vy = JUMP_VELOCITY;
        this.grounded = false;
        this.hunger = Math.max(0, this.hunger - 0.08);
      }
    }

    const moved = moveWithCollisions(world, this.x, this.y, this.z, this.vx * dt, this.vy * dt, this.vz * dt);
    this.x = moved.x;
    this.y = moved.y;
    this.z = moved.z;
    this.grounded = moved.grounded && !this.flying;
    if (moved.hitY) {
      if (this.vy < -12 && !this.flying && !this.swimming) {
        const dmg = Math.floor((-this.vy - 12) * 0.7);
        this.hurt(dmg);
      }
      this.vy = 0;
    }
    if (this.grounded || this.swimming || this.flying) this.fallStart = this.y;
    if (this.y < -8) {
      this.hurt(4);
      this.y = SEA_LEVEL + 8;
      this.vy = 0;
    }

    if (this.sprinting && (mx || mz)) this.hunger = Math.max(0, this.hunger - dt * 0.25);
    if (this.hunger > 16 && this.health < MAX_HEALTH) {
      this.health = Math.min(MAX_HEALTH, this.health + dt * 0.4);
    }
    if (this.hunger <= 0 && this.health > 1) {
      this.hurt(dt * 0.5);
    }

    const eye = this.eye();
    const dir = this.lookDir();
    this.target = raycastVoxel(world, eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, 6.2);

    const clicks = input.consumeClicks();
    if (clicks.left && onPunch()) {
      this.breakTime = 0;
    } else if (input.left && this.target) {
      this.progressBreak(dt, world, onBreak);
    } else {
      this.breakTime = 0;
      this.breakTarget = null;
    }

    if (clicks.right) {
      this.tryUse(world, containers, onPlace, onInteract);
    }

    const held = this.inventory.selectedSlot();
    const heldColor = held ? colorOf(held.id) : null;
    const moving = Math.abs(this.vx) + Math.abs(this.vz) > 0.2;
    this.character.update(
      dt,
      moving,
      this.grounded,
      this.flying,
      this.swimming,
      this.yaw,
      this.pitch,
      this.inventory.armor,
      heldColor,
    );
    this.character.group.position.set(this.x, this.y + this.character.bob, this.z);
    this.character.setVisible(this.thirdPerson);
  }

  private progressBreak(dt: number, world: World, onBreak: (hit: RayHit) => void): void {
    const hit = this.target;
    if (!hit) return;
    if (blockDef(hit.id).hardness < 0) return;
    if (
      !this.breakTarget ||
      this.breakTarget.x !== hit.x ||
      this.breakTarget.y !== hit.y ||
      this.breakTarget.z !== hit.z
    ) {
      this.breakTarget = hit;
      this.breakTime = 0;
    }
    const tool = this.inventory.selectedSlot();
    const speed = breakSpeed(hit.id, tool ? tool.id : null);
    this.breakTime += dt * speed;
    if (this.breakTime >= 1) {
      if (canHarvest(hit.id, tool ? tool.id : null)) onBreak(hit);
      else world.setBlock(hit.x, hit.y, hit.z, Block.AIR);
      if (tool && itemDef(tool.id).durability) this.inventory.damageSelected(1);
      this.breakTime = 0;
      this.breakTarget = null;
    }
  }

  private tryUse(
    world: World,
    _containers: Containers,
    onPlace: (x: number, y: number, z: number, id: number) => void,
    onInteract: (hit: RayHit) => void,
  ): void {
    const hit = this.target;
    const held = this.inventory.selectedSlot();
    if (hit) {
      const id = world.getBlock(hit.x, hit.y, hit.z);
      if (id === Block.CRAFTING_TABLE || id === Block.CHEST || id === Block.FURNACE) {
        onInteract(hit);
        return;
      }
    }
    if (held && itemDef(held.id).food) {
      const food = itemDef(held.id).food!;
      if (this.hunger < MAX_HUNGER) {
        this.hunger = Math.min(MAX_HUNGER, this.hunger + food.hunger);
        this.health = Math.min(MAX_HEALTH, this.health + food.heal);
        this.inventory.consumeSelected(1);
      }
      return;
    }
    if (held && itemDef(held.id).armorSlot) {
      const left = this.inventory.tryWear(held);
      this.inventory.hotbar[this.inventory.selected] = left;
      return;
    }
    if (held && itemDef(held.id).place && hit) {
      const px = hit.x + hit.nx;
      const py = hit.y + hit.ny;
      const pz = hit.z + hit.nz;
      const dest = world.getBlock(px, py, pz);
      if (!isReplaceable(dest) && dest !== Block.AIR) return;
      const destBox = {
        minX: px,
        maxX: px + 1,
        minY: py,
        maxY: py + 1,
        minZ: pz,
        maxZ: pz + 1,
      };
      const me = playerBox(this.x, this.y, this.z);
      const overlap =
        me.minX < destBox.maxX &&
        me.maxX > destBox.minX &&
        me.minY < destBox.maxY &&
        me.maxY > destBox.minY &&
        me.minZ < destBox.maxZ &&
        me.maxZ > destBox.minZ;
      if (overlap && isSolid(itemDef(held.id).place!)) return;
      onPlace(px, py, pz, itemDef(held.id).place!);
      this.inventory.consumeSelected(1);
    }
  }

  hurt(amount: number): void {
    if (amount <= 0) return;
    if (this.time < this.invincibleUntil) return;
    const armor = this.inventory.armorPoints();
    const reduced = amount * (1 - Math.min(0.8, armor / 25));
    this.health = Math.max(0, this.health - reduced);
    this.invincibleUntil = this.time + 0.6;
    this.hurtFlash = 0.25;
  }

  applyCamera(camera: THREE.PerspectiveCamera, world: World): void {
    const eye = this.eye();
    const dir = this.lookDir();
    if (!this.thirdPerson) {
      camera.position.copy(eye);
      camera.lookAt(eye.clone().add(dir));
      return;
    }
    const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    const desired = eye
      .clone()
      .addScaledVector(dir, -THIRD_PERSON_DIST)
      .addScaledVector(right, 0.45);
    desired.y += 0.55;
    const boom = desired.clone().sub(eye);
    const boomLen = boom.length() || 1;
    boom.multiplyScalar(1 / boomLen);
    const hit = raycastVoxel(world, eye.x, eye.y, eye.z, boom.x, boom.y, boom.z, boomLen);
    const dist = hit ? Math.max(1.8, hit.dist - 0.35) : Math.max(1.8, boomLen);
    const pos = eye.clone().addScaledVector(boom, dist);
    camera.position.lerp(pos, 0.28);
    const toCam = camera.position.clone().sub(eye);
    if (toCam.length() < 1.6) {
      toCam.setLength(1.6);
      camera.position.copy(eye).add(toCam);
    }
    camera.lookAt(eye.x, eye.y - 0.1, eye.z);
  }
}

function colorOf(id: number): number {
  const c = itemDef(id).color;
  return (c[0] << 16) | (c[1] << 8) | c[2];
}
